import type {
  MultiMonitorCapability,
  ChildWindowState,
  ScreenRole,
  SerializedOHIFProtocol,
} from './types';
import { HEARTBEAT_INTERVAL_MS } from './types';
import { MonitorChannel } from './MonitorChannel';

// ---------------------------------------------------------------------------
// Window Management API type shims (not yet in lib.dom.d.ts)
// ---------------------------------------------------------------------------

interface ScreenDetailed {
  readonly availHeight: number;
  readonly availLeft: number;
  readonly availTop: number;
  readonly availWidth: number;
  readonly height: number;
  readonly width: number;
  readonly left: number;
  readonly top: number;
  readonly devicePixelRatio: number;
  readonly isPrimary: boolean;
  readonly isInternal: boolean;
  readonly label: string;
}

interface ScreenDetails extends EventTarget {
  readonly screens: ReadonlyArray<ScreenDetailed>;
  readonly currentScreen: ScreenDetailed;
  addEventListener(
    type: 'screenschange',
    listener: EventListenerOrEventListenerObject,
    options?: boolean | AddEventListenerOptions,
  ): void;
  removeEventListener(
    type: 'screenschange',
    listener: EventListenerOrEventListenerObject,
    options?: boolean | EventListenerOptions,
  ): void;
}

// Re-export the local ScreenDetailed so consumers don't need the shim
export type { ScreenDetailed };

// ---------------------------------------------------------------------------
// MonitorManager
// ---------------------------------------------------------------------------

type ScreenChangeCallback = (screens: ScreenDetailed[]) => void;
type ChildChangeCallback = (children: ChildWindowState[]) => void;

/**
 * Core multi-monitor orchestrator.
 *
 * Responsibilities:
 *  - Feature-detect the Window Management API
 *  - Request user permission
 *  - Enumerate connected screens
 *  - Open / close child viewer windows on specific screens
 *  - Maintain a registry of open child windows
 *  - Emit heartbeats via the MonitorChannel
 */
export class MonitorManager {
  private screenDetails: ScreenDetails | null = null;
  private children = new Map<string, ChildWindowState>();
  private screenChangeListeners = new Set<ScreenChangeCallback>();
  private childChangeListeners = new Set<ChildChangeCallback>();
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private pollingTimer: ReturnType<typeof setInterval> | null = null;

  readonly channel = new MonitorChannel();

  // ── Feature detection ───────────────────────────────────────────

  /** Check whether the browser + environment supports multi-monitor. */
  async checkCapability(): Promise<MultiMonitorCapability> {
    const apiSupported = 'getScreenDetails' in window;

    if (!apiSupported) {
      return { apiSupported: false, permissionGranted: false, multipleScreens: false, available: false };
    }

    let permissionGranted = false;
    try {
      const status = await navigator.permissions.query({ name: 'window-management' as PermissionName });
      permissionGranted = status.state === 'granted';
    } catch {
      // Permission API may not support this name — fall back to false
    }

    // If permission is already granted but we haven't fetched screen details
    // yet (e.g. permission was granted on a previous page load), do it now.
    if (permissionGranted && !this.screenDetails) {
      try {
        this.screenDetails = await (window as unknown as { getScreenDetails: () => Promise<ScreenDetails> })
          .getScreenDetails();
        this.screenDetails.addEventListener('screenschange', this.handleScreensChange);
        this.channel.open();
        this.startHeartbeat();
        this.startChildPolling();
      } catch {
        // Fall through — screens will remain empty
      }
    }

    const multipleScreens = (window.screen as unknown as { isExtended?: boolean }).isExtended === true;

    return {
      apiSupported,
      permissionGranted,
      multipleScreens,
      available: apiSupported && permissionGranted && multipleScreens,
    };
  }

  // ── Permission request ──────────────────────────────────────────

  /**
   * Trigger the browser permission prompt.
   * **Must be called from a user gesture** (click handler, etc.).
   * Returns true if permission was granted.
   */
  async requestPermission(): Promise<boolean> {
    try {
      this.screenDetails = await (window as unknown as { getScreenDetails: () => Promise<ScreenDetails> })
        .getScreenDetails();

      // Wire up screen-change events
      this.screenDetails.addEventListener('screenschange', this.handleScreensChange);

      // Start channel + heartbeat
      this.channel.open();
      this.startHeartbeat();
      this.startChildPolling();

      return true;
    } catch {
      return false;
    }
  }

  // ── Screen enumeration ──────────────────────────────────────────

  /** Return the list of detected screens. Empty if permission not yet granted. */
  getScreens(): ScreenDetailed[] {
    if (!this.screenDetails) return [];
    return Array.from(this.screenDetails.screens);
  }

  /** Subscribe to screen connect/disconnect events. Returns unsubscribe fn. */
  onScreenChange(callback: ScreenChangeCallback): () => void {
    this.screenChangeListeners.add(callback);
    return () => { this.screenChangeListeners.delete(callback); };
  }

  /** Subscribe to child-window state changes. Returns unsubscribe fn. */
  onChildChange(callback: ChildChangeCallback): () => void {
    this.childChangeListeners.add(callback);
    return () => { this.childChangeListeners.delete(callback); };
  }

  // ── Window lifecycle ────────────────────────────────────────────

  /**
   * Open a viewer child window on a specific screen.
   *
   * The child window loads `/viewer-standalone` — a lightweight route that
   * renders a full-bleed viewer iframe (no worklist, no tabs).
   * The viewer type is passed via query param and resolved by StandaloneViewer.
   */
  openViewerOnScreen(
    screen: ScreenDetailed,
    studyInstanceUID: string,
    siteId: string,
    role: ScreenRole = 'secondary-viewer',
    viewerId?: string,
    objKey?: string,
  ): ChildWindowState | null {
    const windowId = `monitor-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const params = new URLSearchParams({
      studyUID: studyInstanceUID,
      site: siteId,
      role,
      windowId,
    });
    if (viewerId) {
      params.set('viewer', viewerId);
    }
    if (objKey) {
      params.set('objKey', objKey);
    }

    const url = `/viewer-standalone?${params.toString()}`;

    // Position the popup to fill the target screen.
    // The 'popup' feature tells the browser this is a standalone window
    // (no address bar / tabs in some browsers). We request fullscreen
    // via the Window Management API feature string when available.
    const features = [
      `left=${screen.availLeft}`,
      `top=${screen.availTop}`,
      `width=${screen.availWidth}`,
      `height=${screen.availHeight}`,
      'popup',
    ].join(',');

    const win = window.open(url, windowId, features);
    if (!win) {
      // Popup was blocked
      return null;
    }

    // Once the child window loads, request fullscreen so it covers the
    // entire target screen with no chrome.  This requires a user-gesture
    // context — the original study-open click satisfies that requirement
    // as long as we call it synchronously or within a short timeout.
    try {
      win.addEventListener('load', () => {
        try {
          win.document.documentElement
            .requestFullscreen({ screen } as FullscreenOptions)
            .catch(() => {
              // Fullscreen denied (e.g. permission policy) — the window
              // is already sized to cover the screen, so this is fine.
            });
        } catch {
          // requestFullscreen not available — ignore
        }
      });
    } catch {
      // Cross-origin or security restriction — ignore
    }

    const state: ChildWindowState = {
      windowId,
      screenLabel: screen.label || `${screen.width}x${screen.height}`,
      role,
      windowRef: win,
      studyInstanceUID,
      alive: true,
    };

    this.children.set(windowId, state);
    this.notifyChildChange();

    // Announce via BroadcastChannel
    this.channel.send({
      type: 'WINDOW_OPENED',
      windowId,
      role,
      screenLabel: state.screenLabel,
    });

    return state;
  }

  /** Close a specific child window. */
  closeWindow(windowId: string): void {
    const state = this.children.get(windowId);
    if (!state) return;

    try {
      state.windowRef.close();
    } catch {
      // Window may already be closed
    }

    this.children.delete(windowId);
    this.notifyChildChange();

    this.channel.send({ type: 'WINDOW_CLOSING', windowId });
  }

  /** Close all child windows. */
  closeAll(): void {
    for (const [windowId, state] of this.children) {
      try { state.windowRef.close(); } catch { /* already closed */ }
      this.channel.send({ type: 'WINDOW_CLOSING', windowId });
    }
    this.children.clear();
    this.notifyChildChange();
  }

  /** Get a snapshot of all open child windows. */
  getChildren(): ChildWindowState[] {
    return Array.from(this.children.values());
  }

  /** Find a child window by role. */
  getWindowForRole(role: ScreenRole): ChildWindowState | undefined {
    return Array.from(this.children.values()).find((c) => c.role === role && c.alive);
  }

  // ── Multi-screen protocol distribution (Phase 3) ─────────────────

  /**
   * Send a pre-translated OHIF protocol to a specific child window.
   * The protocol distribution logic lives in protocolDistributor.ts;
   * this method is the transport layer.
   */
  sendProtocolToWindow(
    targetWindowId: string,
    studyUID: string,
    ohifProtocol: SerializedOHIFProtocol,
  ): void {
    this.channel.send({
      type: 'LOAD_STUDY_WITH_PROTOCOL',
      targetWindowId,
      studyUID,
      ohifProtocol,
    });
  }

  // ── Cleanup ─────────────────────────────────────────────────────

  /** Tear down listeners, channels, and child windows. */
  destroy(): void {
    this.closeAll();

    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    if (this.pollingTimer) clearInterval(this.pollingTimer);
    this.heartbeatTimer = null;
    this.pollingTimer = null;

    if (this.screenDetails) {
      this.screenDetails.removeEventListener('screenschange', this.handleScreensChange);
      this.screenDetails = null;
    }

    this.channel.close();
    this.screenChangeListeners.clear();
    this.childChangeListeners.clear();
  }

  // ── Private helpers ─────────────────────────────────────────────

  private handleScreensChange = (): void => {
    const screens = this.getScreens();
    for (const listener of this.screenChangeListeners) {
      try { listener(screens); } catch { /* */ }
    }
  };

  private startHeartbeat(): void {
    if (this.heartbeatTimer) return;
    this.heartbeatTimer = setInterval(() => {
      this.channel.send({ type: 'HEARTBEAT', windowId: 'primary', role: 'worklist' });
    }, HEARTBEAT_INTERVAL_MS);
  }

  /** Poll child window handles to detect independently-closed windows. */
  private startChildPolling(): void {
    if (this.pollingTimer) return;
    this.pollingTimer = setInterval(() => {
      let changed = false;
      for (const [id, state] of this.children) {
        try {
          if (state.windowRef.closed) {
            this.children.delete(id);
            changed = true;
          }
        } catch {
          // Cross-origin access error — assume closed
          this.children.delete(id);
          changed = true;
        }
      }
      if (changed) this.notifyChildChange();
    }, 2000);
  }

  private notifyChildChange(): void {
    const children = this.getChildren();
    for (const listener of this.childChangeListeners) {
      try { listener(children); } catch { /* */ }
    }
  }
}
